import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { ResultRegularizerModal } from "../components/ui/profile/result-regularizer-modal";
import { EditPersonalInfoModal } from "@/components/ui/profile/edit-personal-info-modal";
import { Sidebar } from "../components/ui/sidebar";
import ProtectedRoute from "../components/ProtectedRoute";
import { useAuth, AuthStore } from "../hooks/stores/AuthStore";
import { StudentStore } from "@/hooks/stores/StudentStore";
import { StorageService } from "@/hooks/services/storageService";
import {
  User, Mail, Phone, Cake, School, MapPin, Upload, FileText,
  CheckCircle2, Clock, XCircle, Pencil, ShieldCheck, Brain,
  Briefcase, BookOpen, TrendingUp, Star, Zap, Target,
  Beaker, Calculator, Palette, BookMarked,
} from "lucide-react";
import traitListData from "../../traitList.json";

// ─── Lookups ──────────────────────────────────────────────────────────────────

const TRAIT_FULL = Object.fromEntries(
  traitListData.map((t) => [t.id, { name: t.full_name, cluster: t.cluster }])
);

const CLUSTER_COLORS = {
  STEM:       { bar: "bg-blue-500",    text: "text-blue-600",    light: "bg-blue-50"    },
  Humanities: { bar: "bg-rose-500",    text: "text-rose-600",    light: "bg-rose-50"    },
  Management: { bar: "bg-amber-500",   text: "text-amber-600",   light: "bg-amber-50"   },
  all:        { bar: "bg-emerald-500", text: "text-emerald-600", light: "bg-emerald-50" },
};

const INTEREST_COLORS = [
  { bar: "bg-slate-800",   text: "text-slate-700"   },
  { bar: "bg-amber-400",   text: "text-amber-600"   },
  { bar: "bg-orange-500",  text: "text-orange-600"  },
  { bar: "bg-blue-400",    text: "text-blue-600"    },
  { bar: "bg-teal-500",    text: "text-teal-600"    },
  { bar: "bg-violet-500",  text: "text-violet-600"  },
];

const SUBJECT_COLORS = ["bg-blue-500", "bg-rose-500", "bg-emerald-500", "bg-amber-500", "bg-purple-500"];
const DEFAULT_SUBJECTS = [
  { subject: "Mathematics", percentage: 85, color: "bg-blue-500" },
  { subject: "English",     percentage: 78, color: "bg-rose-500" },
  { subject: "Science",     percentage: 82, color: "bg-emerald-500" },
  { subject: "History",     percentage: 75, color: "bg-amber-500" },
  { subject: "Art & Design",percentage: 90, color: "bg-purple-500" },
];

const STATUS_CONFIG = {
  pending:  { icon: Clock,        color: "text-amber-600",   bg: "bg-amber-50",   border: "border-amber-200",   label: "Pending" },
  verified: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", label: "Verified" },
  rejected: { icon: XCircle,      color: "text-red-600",     bg: "bg-red-50",     border: "border-red-200",     label: "Rejected" },
};

const AVATAR_BG_PAIRS = [
  { ring: "bg-violet-100", fill: "from-violet-500 to-purple-600" },
  { ring: "bg-teal-100",   fill: "from-teal-500 to-emerald-600"  },
  { ring: "bg-blue-100",   fill: "from-blue-500 to-indigo-600"   },
  { ring: "bg-rose-100",   fill: "from-rose-500 to-pink-600"     },
  { ring: "bg-amber-100",  fill: "from-amber-500 to-orange-600"  },
];

function formatDate(d) {
  if (!d) return "Not provided";
  try {
    return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "Not provided";
  }
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyWidget({ icon: Icon, message, action, onAction }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
        <Icon size={18} className="text-slate-300" />
      </div>
      <p className="text-xs text-slate-400 font-medium max-w-40 leading-snug">{message}</p>
      {action && (
        <button
          onClick={onAction}
          className="text-[11px] font-semibold text-teal-600 hover:text-teal-700 underline underline-offset-2"
        >
          {action}
        </button>
      )}
    </div>
  );
}

// ─── Info row ─────────────────────────────────────────────────────────────────

function InfoRow({ icon: Icon, iconBg, iconColor, value }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className={`w-8 h-8 rounded-full ${iconBg} flex items-center justify-center shrink-0`}>
        <Icon size={14} className={iconColor} />
      </div>
      <span className="text-xs text-slate-700 font-medium truncate">{value || "Not provided"}</span>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const Profile = () => {
  const { isHydrated } = useAuth();
  const navigate = useNavigate();
  const student = StudentStore((s) => s.student);
  const currentUser = AuthStore((s) => s.currentUser);

  const [isUploadOpen, setIsUploadOpen]   = useState(false);
  const [isEditOpen,   setIsEditOpen]     = useState(false);
  const [resultsVersion, setResultsVersion] = useState(0);

  // ── Derived identity ────────────────────────────────────────────────────────
  const firstName = student?.firstName || currentUser?.name?.split(" ")[0] || "Student";
  const lastName  = student?.lastName  || currentUser?.name?.split(" ").slice(1).join(" ") || "";
  const fullName  = `${firstName} ${lastName}`.trim();
  const handle    = currentUser?.email?.split("@")[0] || "student";
  const initials  = fullName.split(" ").map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const avatarPair = AVATAR_BG_PAIRS[fullName.charCodeAt(0) % AVATAR_BG_PAIRS.length];

  // ── Trait profile ───────────────────────────────────────────────────────────
  const topTraits = useMemo(() => {
    if (!student?.traitProfile) return [];
    return Object.entries(student.traitProfile)
      .map(([id, score]) => ({
        id,
        score: typeof score === "number" ? Math.round(score) : 0,
        name: TRAIT_FULL[id]?.name || id,
        cluster: TRAIT_FULL[id]?.cluster || "all",
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);
  }, [student?.traitProfile]);

  // ── Career interests ────────────────────────────────────────────────────────
  const careerInterests = student?.careerInterests || [];

  // ── Academic performance ────────────────────────────────────────────────────
  const subjects = useMemo(() => {
    const raw = student?.academicResults;
    if (!raw || raw.length === 0) return DEFAULT_SUBJECTS;
    return raw.map((r, i) => ({
      subject: r.subject ?? "Unknown",
      percentage: typeof r.score === "number" ? Math.min(100, r.score) : 0,
      color: SUBJECT_COLORS[i % SUBJECT_COLORS.length],
    }));
  }, [student?.academicResults]);

  // ── Uploaded results ────────────────────────────────────────────────────────
  const academicResults = useMemo(() => {
    if (!currentUser) return [];
    const students = StorageService.read("students");
    const s = students?.find(
      (st) => st.userId === currentUser._id || st.email === currentUser.email
    );
    return s?.academicResults ?? [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, resultsVersion]);

  // ── Stats ───────────────────────────────────────────────────────────────────
  const streakDays   = currentUser?.streakDays ?? 0;
  const totalPoints  = currentUser?.totalPoints ?? 0;
  const level        = currentUser?.level ?? 1;

  if (!isHydrated) {
    return (
      <ProtectedRoute>
        <div className="flex min-h-screen bg-slate-50 items-center justify-center">
          <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />

        <main className="flex-1 lg:ml-64 p-5 overflow-y-auto w-full transition-all duration-300 min-w-0">

          {/* ── Page header ──────────────────────────────────────────────── */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-base font-bold text-slate-900">My Profile</h1>
              <p className="text-xs text-slate-400 mt-0.5">Manage your personal information</p>
            </div>
            <button
              onClick={() => setIsEditOpen(true)}
              className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 active:scale-95 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-all shadow-sm"
            >
              <Pencil size={12} /> Edit Profile
            </button>
          </div>

          {/* ── Main grid ────────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-5 items-start">

            {/* ── LEFT: widget cards ──────────────────────────────────────── */}
            <div className="xl:col-span-3 space-y-5">

              {/* Row 1 — Trait Profile + Career Interests */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                {/* Trait Profile */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center">
                        <Brain size={14} className="text-violet-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-900">Trait Profile</h3>
                        <p className="text-[11px] text-slate-400">Based on assessments</p>
                      </div>
                    </div>
                  </div>

                  {topTraits.length > 0 ? (
                    <div className="space-y-3">
                      {topTraits.map((trait) => {
                        const c = CLUSTER_COLORS[trait.cluster] ?? CLUSTER_COLORS.all;
                        return (
                          <div key={trait.id}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-slate-600 truncate mr-2">{trait.name}</span>
                              <span className={`text-[11px] font-bold shrink-0 ${c.text}`}>{trait.score}%</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-1.5">
                              <div
                                className={`${c.bar} h-1.5 rounded-full transition-all duration-500`}
                                style={{ width: `${trait.score}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyWidget
                      icon={Brain}
                      message="Take the assessment to reveal your strengths"
                      action="Start assessment"
                      onAction={() => navigate("/psychometric-assessment")}
                    />
                  )}
                </div>

                {/* Career Interests */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-teal-50 flex items-center justify-center">
                        <Briefcase size={14} className="text-teal-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-900">Career Interests</h3>
                        <p className="text-[11px] text-slate-400">Based on your choices</p>
                      </div>
                    </div>
                  </div>

                  {careerInterests.length > 0 ? (
                    <div className="space-y-3">
                      {careerInterests.slice(0, 6).map((interest, i) => {
                        const c = INTEREST_COLORS[i % INTEREST_COLORS.length];
                        const pct = Math.max(35, 88 - i * 9);
                        return (
                          <div key={interest}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-slate-600 truncate mr-2">{interest}</span>
                              <span className={`text-[11px] font-bold shrink-0 ${c.text}`}>{pct}%</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-1.5">
                              <div
                                className={`${c.bar} h-1.5 rounded-full`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyWidget
                      icon={Briefcase}
                      message="Explore careers to build your interests list"
                      action="Explore careers"
                      onAction={() => navigate("/career-explorer")}
                    />
                  )}
                </div>
              </div>

              {/* Row 2 — Academic Performance */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                      <TrendingUp size={14} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">Academic Performance</h3>
                      <p className="text-[11px] text-slate-400">{subjects.length} subjects tracked</p>
                    </div>
                  </div>
                  <span className="text-[11px] text-slate-400 font-medium">
                    Avg {Math.round(subjects.reduce((s, r) => s + r.percentage, 0) / subjects.length)}%
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {subjects.map((s) => (
                    <div key={s.subject}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-600">{s.subject}</span>
                        <span className="text-xs font-bold text-slate-700">{s.percentage}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-1.5">
                        <div
                          className={`${s.color} h-1.5 rounded-full transition-all duration-500`}
                          style={{ width: `${s.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Row 3 — Academic Results */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                      <FileText size={14} className="text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">Academic Results</h3>
                      <p className="text-[11px] text-slate-400">Upload for school verification</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsUploadOpen(true)}
                    className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 active:scale-95 text-white text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-all"
                  >
                    <Upload size={11} /> Upload
                  </button>
                </div>

                {academicResults.length === 0 ? (
                  <EmptyWidget
                    icon={FileText}
                    message="No results uploaded yet — WAEC, JAMB or school reports"
                    action="Upload a result"
                    onAction={() => setIsUploadOpen(true)}
                  />
                ) : (
                  <div className="space-y-2">
                    {academicResults.map((r) => {
                      const cfg = STATUS_CONFIG[r.status] ?? STATUS_CONFIG.pending;
                      const Icon = cfg.icon;
                      return (
                        <div
                          key={r.id}
                          className={`flex items-center gap-2.5 p-2.5 rounded-xl border ${cfg.bg} ${cfg.border}`}
                        >
                          <FileText size={13} className={cfg.color} />
                          <span className="flex-1 text-xs font-semibold text-slate-800 truncate">{r.label}</span>
                          <span className={`flex items-center gap-1 text-[10px] font-bold shrink-0 ${cfg.color}`}>
                            <Icon size={10} /> {cfg.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ── RIGHT: profile card ──────────────────────────────────────── */}
            <div className="xl:col-span-2 xl:sticky xl:top-5 space-y-4">

              {/* Profile card */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">

                {/* Avatar */}
                <div className="flex flex-col items-center pt-8 pb-5 px-6">
                  <div className={`w-28 h-28 rounded-full ${avatarPair.ring} flex items-center justify-center mb-4`}>
                    <div
                      className={`w-24 h-24 rounded-full bg-linear-to-br ${avatarPair.fill} flex items-center justify-center text-white text-3xl font-black select-none`}
                    >
                      {initials}
                    </div>
                  </div>
                  <h2 className="text-lg font-bold text-slate-900 text-center">{fullName}</h2>
                  <p className="text-xs text-slate-400 mt-0.5">@{handle}</p>

                  {student?.classLevel && (
                    <span className="mt-2.5 inline-flex items-center gap-1 bg-teal-50 text-teal-700 text-[11px] font-semibold px-3 py-1 rounded-full border border-teal-200">
                      <School size={10} /> {student.classLevel}
                    </span>
                  )}
                </div>

                {/* Mini stats row */}
                <div className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100 mb-1">
                  {[
                    { icon: Zap,    color: "text-amber-500",   label: "Level",  value: level        },
                    { icon: Target, color: "text-teal-500",    label: "Points", value: totalPoints  },
                    { icon: Star,   color: "text-orange-500",  label: "Streak", value: `${streakDays}d` },
                  ].map(({ icon: Icon, color, label, value }) => (
                    <div key={label} className="flex flex-col items-center py-3">
                      <Icon size={14} className={color} />
                      <span className="text-sm font-bold text-slate-900 mt-0.5">{value}</span>
                      <span className="text-[10px] text-slate-400">{label}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-slate-100" />

                {/* Personal info */}
                <div className="px-5 py-4 space-y-3">
                  <InfoRow
                    icon={Cake}
                    iconBg="bg-orange-100"
                    iconColor="text-orange-600"
                    value={formatDate(currentUser?.dateOfBirth)}
                  />
                  <InfoRow
                    icon={School}
                    iconBg="bg-purple-100"
                    iconColor="text-purple-600"
                    value={currentUser?.schoolName || "School not set"}
                  />
                  <InfoRow
                    icon={MapPin}
                    iconBg="bg-teal-100"
                    iconColor="text-teal-600"
                    value={currentUser?.address || "Address not set"}
                  />
                  <InfoRow
                    icon={Mail}
                    iconBg="bg-blue-100"
                    iconColor="text-blue-600"
                    value={currentUser?.email}
                  />
                  {currentUser?.phoneNumber && (
                    <InfoRow
                      icon={Phone}
                      iconBg="bg-rose-100"
                      iconColor="text-rose-600"
                      value={currentUser.phoneNumber}
                    />
                  )}
                </div>

                <div className="border-t border-slate-100 mx-5" />

                {/* About */}
                <div className="px-5 py-4">
                  <h4 className="text-xs font-bold text-slate-900 mb-2">About Me</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    {currentUser?.bio ||
                      "No bio added yet. Click Edit Profile to add a short description about yourself."}
                  </p>
                </div>

                {/* Guardian card */}
                {(currentUser?.parentName || currentUser?.parentEmail) && (
                  <div className="px-5 pb-5">
                    <div className="bg-slate-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[11px] font-bold text-slate-300 uppercase tracking-widest">Guardian</span>
                        <ShieldCheck size={13} className="text-slate-500" />
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center shrink-0">
                          <User size={15} className="text-slate-300" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-white truncate">
                            {currentUser.parentName || "—"}
                          </p>
                          <p className="text-[11px] text-slate-400 truncate">
                            {currentUser.parentEmail || currentUser.parentPhone || "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        <EditPersonalInfoModal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} />
        <ResultRegularizerModal
          isOpen={isUploadOpen}
          onClose={() => setIsUploadOpen(false)}
          onSaved={() => setResultsVersion((v) => v + 1)}
        />
      </div>
    </ProtectedRoute>
  );
};

export default Profile;
